interface SpeedTestResult {
  download: number;
  upload: number;
  ping: number;
  jitter: number;
}

interface ProgressCallback {
  (progress: number, stage: string): void;
}

export class SpeedTestService {
  private testFileUrl = 'https://speed.cloudflare.com/__down?bytes=25000000'; // 25MB test file
  private uploadTestUrl = 'https://speed.cloudflare.com/__up'; // Upload endpoint
  private pingTestUrl = 'https://www.cloudflare.com/cdn-cgi/trace';
  
  // Alternative upload test using Cloudflare trace endpoint
  private alternativeUploadUrl = 'https://www.cloudflare.com/cdn-cgi/trace';

  async runFullSpeedTest(
    onProgress?: ProgressCallback
  ): Promise<SpeedTestResult> {
    try {
      onProgress?.(10, 'Mjerenje ping-a...');
      const { ping, jitter } = await this.measurePingAndJitter();

      onProgress?.(40, 'Testiranje brzine preuzimanja...');
      const download = await this.measureDownloadSpeed();

      onProgress?.(70, 'Testiranje brzine slanja...');
      const upload = await this.measureUploadSpeed(download);

      onProgress?.(100, 'Završeno!');

      return {
        download: Math.round(download * 10) / 10,
        upload: Math.round(upload * 10) / 10,
        ping: Math.round(ping),
        jitter: Math.round(jitter * 10) / 10,
      };
    } catch (error) {
      console.error('Speed test error:', error);
      throw new Error('Greška pri testiranju brzine. Molimo pokušajte ponovo.');
    }
  }

  private async measurePingAndJitter(): Promise<{
    ping: number;
    jitter: number;
  }> {
    const measurements: number[] = [];
    const numTests = 5;

    for (let i = 0; i < numTests; i++) {
      const start = performance.now();
      try {
        await fetch(this.pingTestUrl, {
          method: 'GET',
          cache: 'no-cache',
        });
        const end = performance.now();
        measurements.push(end - start);
      } catch (error) {
        console.warn('Ping measurement failed:', error);
      }

      // Small delay between measurements
      if (i < numTests - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (measurements.length === 0) {
      throw new Error('Unable to measure ping');
    }

    const avgPing =
      measurements.reduce((a, b) => a + b, 0) / measurements.length;

    // Calculate jitter (standard deviation)
    const variance =
      measurements.reduce((sum, val) => sum + Math.pow(val - avgPing, 2), 0) /
      measurements.length;
    const jitter = Math.sqrt(variance);

    return { ping: avgPing, jitter };
  }

  private async measureDownloadSpeed(): Promise<number> {
    const fileSizeMB = 25; // 25MB test file
    const start = performance.now();

    try {
      const response = await fetch(this.testFileUrl, {
        method: 'GET',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error('Download test failed');
      }

      // Read the response body
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Unable to read response');
      }

      let receivedBytes = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        receivedBytes += value?.length || 0;
      }

      const end = performance.now();
      const durationSeconds = (end - start) / 1000;
      const speedMbps = (receivedBytes * 8) / (durationSeconds * 1000000);

      return speedMbps;
    } catch (error) {
      console.error('Download speed test error:', error);
      // Fallback to smaller test if main test fails
      return await this.fallbackDownloadTest();
    }
  }

  private async fallbackDownloadTest(): Promise<number> {
    const smallFileUrl = 'https://speed.cloudflare.com/__down?bytes=5000000'; // 5MB
    const start = performance.now();

    try {
      const response = await fetch(smallFileUrl, {
        method: 'GET',
        cache: 'no-cache',
      });

      await response.blob();
      const end = performance.now();
      const durationSeconds = (end - start) / 1000;
      const speedMbps = (5 * 8) / durationSeconds; // 5MB in Mbps

      return speedMbps;
    } catch (error) {
      console.error('Fallback download test failed:', error);
      throw error;
    }
  }

  private async measureUploadSpeed(downloadSpeed: number): Promise<number> {
    try {
      // Try primary upload test with smaller data size and timeout
      const result = await Promise.race([
        this.performUploadTest(),
        new Promise<number>((_, reject) => 
          setTimeout(() => reject(new Error('Upload test timeout')), 15000)
        )
      ]);
      return result;
    } catch (error) {
      console.warn('Primary upload test failed, using estimation:', error);
      // Fall back to estimation based on download speed
      // Typical upload is 20-40% of download for most connections
      const estimatedUpload = downloadSpeed * 0.3;
      return Math.max(estimatedUpload, 10); // Minimum 10 Mbps estimate
    }
  }

  private async performUploadTest(): Promise<number> {
    // Use smaller data chunks and multiple attempts
    const chunkSize = 1 * 1024 * 1024; // 1MB chunks
    const numChunks = 3;
    const speeds: number[] = [];

    for (let i = 0; i < numChunks; i++) {
      const testData = new Uint8Array(chunkSize);
      // Fill with random data to prevent compression
      crypto.getRandomValues(testData);
      
      const start = performance.now();
      
      try {
        const response = await fetch(this.alternativeUploadUrl, {
          method: 'POST',
          body: testData,
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          cache: 'no-cache',
          mode: 'no-cors', // Use no-cors mode to avoid CORS issues
        });

        const end = performance.now();
        const durationSeconds = (end - start) / 1000;
        const speedMbps = (chunkSize * 8) / (durationSeconds * 1000000);
        speeds.push(speedMbps);
      } catch (error) {
        console.warn(`Upload chunk ${i + 1} failed:`, error);
        // Continue to next chunk
      }

      // Small delay between chunks
      if (i < numChunks - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    if (speeds.length === 0) {
      throw new Error('All upload attempts failed');
    }

    // Return average of successful uploads
    return speeds.reduce((a, b) => a + b, 0) / speeds.length;
  }
}

export const speedTestService = new SpeedTestService();